import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Row, Col, Alert, Spinner, Modal } from 'react-bootstrap';
import { api } from '../../../shared/api';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';

const RentalSettingsCustomer = ({ rental, setRental }) => {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [formData, setFormData] = useState({ customer: '' });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const { isLoading: loading } = useQuery({
    queryKey: ['rental-customers'],
    queryFn: async () => {
      const response = await api.get('customers/api/customers/');
      setCustomers(response);
      if (rental.customer) {
        const customerDetails = response.find(c => c.id === rental.customer);
        setSelectedCustomer(customerDetails);
      }
      setFormData({ customer: rental.customer || '' });
      return response;
    },
    onError: () => setError(t('rentals.settings.customer.updateError')),
  });

  const updateMutation = useMutation({
    mutationFn: () => api.patch(`rentals/api/rentals/${rental.id}/`, { customer: formData.customer }),
    onSuccess: (response) => {
      setRental(response);
      setSuccess(true);
      setShowEditModal(false);
      window.scrollTo(0, 0);
    },
    onError: () => setError(t('rentals.settings.customer.updateError')),
  });
  const submitting = updateMutation.isPending;

  const handleSubmit = (e) => {
    e.preventDefault();
    setSuccess(false);
    setError(null);
    updateMutation.mutate();
  };

  const handleCustomerChange = (e) => {
    const customerId = parseInt(e.target.value);
    setFormData({ customer: customerId });
    const customerDetails = customers.find(c => c.id === customerId);
    setSelectedCustomer(customerDetails);
  };

return (
    <>
      {success && (
        <Alert variant="success" onClose={() => setSuccess(false)} dismissible>
          {t('rentals.settings.customer.updateSuccess')}
        </Alert>
      )}

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      <h2 className="card-title">{t('rentals.settings.customer.title')}</h2>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <div className="customer-details">
          <Row className="g-3 mb-4">
            <Col md={6}>
              <div className="form-label">{t('rentals.settings.customer.company')}</div>
              <input
                type="text"
                className="form-control"
                value={rental.customer_name || ''}
                readOnly
              />
            </Col>
            <Col md={6}>
              <div className="form-label">IČO</div>
              <input
                type="text"
                className="form-control"
                value={selectedCustomer?.ic || '-'}
                readOnly
              />
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col md={6}>
              <div className="form-label">DIČ</div>
              <input
                type="text"
                className="form-control"
                value={selectedCustomer?.dic || '-'}
                readOnly
              />
            </Col>
            <Col md={6}>
              <div className="form-label">{t('rentals.settings.customer.email')}</div>
              <input
                type="text"
                className="form-control"
                value={selectedCustomer?.email || '-'}
                readOnly
              />
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col md={6}>
              <div className="form-label">{t('rentals.settings.customer.phone')}</div>
              <input
                type="text"
                className="form-control"
                value={selectedCustomer?.phone || '-'}
                readOnly
              />
            </Col>
            <Col md={6}>
              <div className="form-label">{t('rentals.settings.customer.address')}</div>
              <input
                type="text"
                className="form-control"
                value={selectedCustomer ? `${selectedCustomer.street || ''}, ${selectedCustomer.city || ''}, ${selectedCustomer.postal_code || ''}` : '-'}
                readOnly
              />
            </Col>
          </Row>

          <div className="d-flex mt-4">
            {rental.customer && (
              <Link to={`/customers/customer/${rental.customer}`} className="btn btn-secondary me-2">
                {t('rentals.settings.customer.name')}
              </Link>
            )}
            <Button variant="primary" onClick={() => setShowEditModal(true)} className="ms-auto">
              {t('rentals.settings.customer.changeCustomer')}
            </Button>
          </div>
        </div>
      )}

      {/* Edit Customer Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('rentals.settings.customer.changeCustomer')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>{t('rentals.settings.customer.customer')}</Form.Label>
              <Form.Select
                value={formData.customer}
                onChange={handleCustomerChange}
                required
              >
                <option value="">{t('rentals.settings.customer.selectCustomer')}</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.company_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            {t('rentals.settings.general.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={submitting || !formData.customer}
          >
            {submitting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {t('common.saving')}
              </>
            ) : (
              t('common.save')
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RentalSettingsCustomer;
